import {
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useContext, useState } from "react";
import usePopup from "../../../hooks/usePopup";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { useMutation } from "react-query";
import {
  dischargeHistorySummary,
  dischargeHistorySummaryv2,
  getAllDocumentsByPatientMasterId,
  getViewDetails,
} from "./services";
import { useParams } from "react-router-dom";
import CustomTableBody from "../../../utilis/component/CustomTableBody";
import DetailsTab from "./DetailsTab";
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import {
  geDisChargeSummary,
  geDisChargeSummaryv2,
} from "../ConsenForms/services";
import DownloadIcon from "@mui/icons-material/Download";
import useAuthentication from "@hooks/useAuthentication";
import { EmrContext } from "../../index";
import type { SetStateFunctionType } from "../../types";
import {
  splitPatientDocumentsPayload,
  UploadedDocumentsSection,
  VIEW_DETAILS_TABLE_HEADING,
} from "./patientDocumentsShared";

function ViewDetails() {
  const { user } = useAuthentication();
  const { CustomModal, showSnackbar } = usePopup();
  const { encounterId, patientId } = useParams();
  const { patientData } = useContext(EmrContext);
  const [id, setId] = useState(0);
  const [openDetails, setShowDetail] = useState(false);
  const [openTab, setOpenTab] = useState(false);
  const [openUploadedDocs, setOpenUploadedDocs] = useState(false);
  const [selected, setSelected] = useState(-1);
  const displayFile = (file: any, title: string) => {
    try {
      const byteCharacters = atob(file);
      const byteArrays = [];

      for (let offset = 0; offset < byteCharacters.length; offset += 512) {
        const slice = byteCharacters.slice(offset, offset + 512);
        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
      }
      let newBlob = new Blob(byteArrays, { type: "application/pdf" });
      const createdBlobUrl = URL.createObjectURL(newBlob);
      const newTab = window.open();
      if (newTab) {
        newTab.document.title = title;
        newTab.location.href = createdBlobUrl;
      }
    } catch (error) {
      showSnackbar({
        message: "Failed to load file !",
        open: true,
        variant: "error",
        duration: 2000,
      });
    }
  };

  const {
    mutate: viewDetails,
    isLoading: detailsLoading,
    data: details,
  } = useMutation(getViewDetails, {
    onError: (error: any) => {
      showSnackbar({
        message: error?.response?.data?.responseMessage || error.message || "",
        open: true,
        variant: "error",
        duration: 3000,
      });
    },
  });

  const {
    mutate: fetchAllPatientDocuments,
    isLoading: allDocsLoading,
    data: allDocsData,
    reset: resetAllDocs,
  } = useMutation(getAllDocumentsByPatientMasterId, {
    onSuccess: (data) => {
      if (data.responseIndicator !== "success" && data.responseMessage) {
        showSnackbar({
          message: data.responseMessage,
          open: true,
          variant: "warning",
          duration: 3000,
        });
      }
    },
    onError: (error: any) => {
      showSnackbar({
        message: error?.response?.data?.responseMessage || error.message || "",
        open: true,
        variant: "error",
        duration: 3000,
      });
    },
  });

  const { registration: registrationDocs, encounter: encounterDocs } =
    allDocsData?.responseIndicator === "success"
      ? splitPatientDocumentsPayload(allDocsData.response)
      : { registration: [], encounter: [] };

  const handleOpenUploadedDocuments = () => {
    const pmId = Number(patientId) || Number(patientData?.pmaster?.id);
    const cid = user?.clinicId;
    if (!pmId || !cid) {
      showSnackbar({
        message: "Patient or clinic information is missing",
        open: true,
        variant: "warning",
        duration: 3000,
      });
      return;
    }
    setOpenUploadedDocs(true);
    fetchAllPatientDocuments({ patientMasterId: pmId, clinicId: cid });
  };

  const handleCloseUploadedDocuments: SetStateFunctionType<boolean> = (
    value,
  ) => {
    setOpenUploadedDocs((prev) => {
      const next = typeof value === "function" ? value(prev) : value;
      if (!next) {
        resetAllDocs();
      }
      return next;
    });
  };

  const handleShowDetails = () => {
    setShowDetail(true);
    viewDetails({
      clinicId: user?.clinicId as number,
      enId: parseInt(encounterId as string),
      pmasterId: parseInt(patientId as string),
    });
  };

  const { mutate: downloadHistorySummary, isLoading: summaryLoading } =
    useMutation(dischargeHistorySummary, {
      onSuccess: (result) => {
        if (result.responseIndicator == "success") {
          displayFile(result.response, "Discharge history Summary");
        } else {
          showSnackbar({
            message: result.responseMessage || "Something went wrong",
            variant: "warning",
            open: true,
            duration: 3000,
          });
        }
      },
      onError: (e: any) => {
        showSnackbar({
          message: e?.response?.data?.responseMessage || "Something went wrong",
          variant: "warning",
          open: true,
          duration: 3000,
        });
      },
    });

  const { mutate: downloadHistorySummaryv2, isLoading: summaryLoadingv2 } =
    useMutation(dischargeHistorySummaryv2, {
      onSuccess: (result) => {
        if (result.responseIndicator == "success") {
          displayFile(result.response, "Discharge history Summary");
        } else {
          showSnackbar({
            message: result.responseMessage || "Something went wrong",
            variant: "warning",
            open: true,
            duration: 3000,
          });
        }
      },
      onError: (e: any) => {
        showSnackbar({
          message: e?.response?.data?.responseMessage || "Something went wrong",
          variant: "warning",
          open: true,
          duration: 3000,
        });
      },
    });

  const { mutate: downloadeDisCharge, isLoading: disChargeLoading } =
    useMutation(geDisChargeSummary, {
      onSuccess: (result) => {
        if (result.responseIndicator == "success") {
          displayFile(result.response, "Discharge Summary form");
        } else {
          showSnackbar({
            message: result.responseMessage,
            open: true,
            variant: "warning",
            duration: 2000,
          });
        }
      },
    });

  const { mutate: downloadeDisChargev2, isLoading: disChargeLoadingv2 } =
    useMutation(geDisChargeSummaryv2, {
      onSuccess: (result) => {
        if (result.responseIndicator == "success") {
          displayFile(result.response, "Discharge Summary form");
        } else {
          showSnackbar({
            message: result.responseMessage,
            open: true,
            variant: "warning",
            duration: 2000,
          });
        }
      },
    });

  const handleOpen = (enId: number) => {
    setId(enId);
    setOpenTab(true);
  };

  return (
    <div>
      <CustomModal
        title="Patient details"
        open={openDetails}
        setOpen={setShowDetail}
      >
        {openTab ? (
          <>
            <div className="p-2 flex justify-end border-b">
              <Button
                variant="text"
                onClick={() => {
                  setOpenTab(false);
                }}
              >
                back
              </Button>
            </div>
            <DetailsTab id={id} />
          </>
        ) : (
          <TableContainer component={Paper}>
            <div className="p-2 flex justify-end gap-2">
              <Button
                onClick={() => {
                  user?.codetype == "CPT"
                    ? downloadHistorySummary(patientId as string)
                    : downloadHistorySummaryv2({
                        id: patientId as string,
                        insname: patientData?.insurancePatient?.insurance_name,
                      });
                }}
                disabled={summaryLoading || summaryLoadingv2}
                endIcon={
                  summaryLoading ? (
                    <CircularProgress size={18} />
                  ) : (
                    <DownloadIcon />
                  )
                }
              >
                Discharge history summary
              </Button>
              <Button
                onClick={handleOpenUploadedDocuments}
                endIcon={<AttachFileIcon />}
              >
                Uploaded documents
              </Button>
            </div>
            <Table size="small" aria-label="simple table">
              <TableHead>
                <TableRow>
                  {VIEW_DETAILS_TABLE_HEADING.map((title) => (
                    <TableCell key={title}>
                      <strong>{title}</strong>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <CustomTableBody
                alert={"No records"}
                dataLoading={detailsLoading}
                isData={Boolean(details?.response.length)}
                totalSpan={{ col: VIEW_DETAILS_TABLE_HEADING.length, row: 5 }}
              >
                {details?.response.map((item, i) => (
                  <TableRow
                    key={"row.name" + i}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>{item.createdDate}</TableCell>
                    <TableCell>{item.PatientID}</TableCell>
                    <TableCell>{item.clcn_name}</TableCell>
                    <TableCell>{item.dept_name}</TableCell>
                    <TableCell>
                      <IconButton
                        disabled={disChargeLoading || disChargeLoadingv2}
                        onClick={() => {
                          user?.codetype == "CPT"
                            ? downloadeDisCharge({
                                id: item.encounterID,
                                tenetClinicId: Number(user?.tenantClinicId),
                              })
                            : downloadeDisChargev2({
                                id: item.encounterID,
                                insname: String(
                                  patientData.insurancePatient?.inPlan_name,
                                ),
                              });
                          setSelected(i);
                        }}
                        color="primary"
                      >
                        {(disChargeLoading || disChargeLoadingv2) &&
                        selected == i ? (
                          <CircularProgress size={18} />
                        ) : (
                          <LocalPrintshopIcon />
                        )}
                      </IconButton>
                      <IconButton
                        onClick={() => {
                          handleOpen(item.encounterID);
                        }}
                        color="success"
                      >
                        <RemoveRedEyeIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </CustomTableBody>
            </Table>
          </TableContainer>
        )}
      </CustomModal>
      <CustomModal
        title="Uploaded documents"
        open={openUploadedDocs}
        setOpen={handleCloseUploadedDocuments}
      >
        <div className="p-2 flex flex-col gap-4 max-h-[75vh] overflow-auto min-w-[min(640px,90vw)]">
          {allDocsLoading ? (
            <div className="flex justify-center py-8">
              <CircularProgress size={32} />
            </div>
          ) : (
            <>
              <UploadedDocumentsSection
                sectionTitle="Registration"
                docs={registrationDocs}
              />
              <UploadedDocumentsSection
                sectionTitle="Encounter"
                docs={encounterDocs}
              />
            </>
          )}
        </div>
      </CustomModal>
      <div className="flex flex-wrap gap-2 items-center">
        <Button onClick={handleShowDetails} endIcon={<RemoveRedEyeIcon />}>
          View Details
        </Button>
      </div>
    </div>
  );
}

export default ViewDetails;
